// Pieza ÚNICA para TODOS los formularios con dirección (cliente, pagador, facturación, empleado, sedes…):
// al escribir un código postal de 5 dígitos pregunta al CRM (GET /api/codigos-postales/{cp}, tabla local
// CNIG+INE, sin servicios externos) y rellena población y provincia.
//
// Solo actúa cuando ALGUIEN ESCRIBE (se llama desde el onChange): al abrir una ficha ya guardada no toca nada.
//
// Uso:
//   const cpAuto = useCodigoPostal();
//   onChange={(e) => { const v = e.target.value; setForm({ ...form, codigo_postal: v });
//     cpAuto.alEscribir(v, ({ poblacion, provincia }) => setForm((f) => ({ ...f, ciudad: poblacion, provincia }))); }}
//   cpAuto.estado       → 'vacio' | 'buscando' | 'ok' | 'no_encontrado'
//   cpAuto.poblaciones  → lista (si un CP abarca varios municipios, para elegir)
import { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../api';

export const normalizarCodigoPostal = (valor) => {
  const cp = String(valor ?? '').replace(/\s+/g, '');
  return /^\d{5}$/.test(cp) ? cp : null; // mientras se escribe (1-4 dígitos) no se consulta nada
};

const cache = new Map();

export async function buscarCodigoPostal(valor) {
  const cp = normalizarCodigoPostal(valor);
  if (!cp) return null;
  if (cache.has(cp)) return cache.get(cp);
  try {
    const r = await axios.get(`${API}/codigos-postales/${cp}`);
    cache.set(cp, r.data);
    return r.data;
  } catch (e) {
    if (e?.response?.status === 404) cache.set(cp, null);
    return null;
  }
}

export default function useCodigoPostal() {
  const [estado, setEstado] = useState('vacio');
  const [resultado, setResultado] = useState(null);
  const pendiente = useRef(null);

  const alEscribir = useCallback((valor, onEncontrado) => {
    const cp = normalizarCodigoPostal(valor);
    if (!cp) {
      pendiente.current = null;
      setEstado('vacio');
      setResultado(null);
      return;
    }
    pendiente.current = cp;
    setEstado('buscando');
    buscarCodigoPostal(cp).then((datos) => {
      if (pendiente.current !== cp) return; // ya escribió otro CP
      if (datos) {
        setResultado(datos);
        setEstado('ok');
        if (onEncontrado) onEncontrado(datos);
      } else {
        setResultado(null);
        setEstado('no_encontrado');
      }
    });
  }, []);

  return {
    alEscribir,
    estado,
    poblaciones: resultado?.poblaciones || [],
    provincia: resultado?.provincia || '',
    resultado,
  };
}
